import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useLocation } from "react-router";
import Dialog from "components/Dialog";
import Button from "components/Button";
import FolderIcon from "assets/images/icons/folder.svg";
import ModelIcon from "assets/images/icons/model.svg";

import { actions as modelActions } from "features/Model/_ducks";
import { getSortableName } from "utils/directory/sorting";

import {
    headerContainer,
    headerText,
    dialogContainer,
    foldersContainer,
    folderImg,
    folderContainer,
    selectedFolderContainer,
    headerTextContainer,
    goBackImg,
    moveToContainer,
    moveToText,
    loading,
    itemLabel,
    labelRowText,
    errorMessage,
} from "features/Model/Upgrades/UpgradesTable/SelectModel/style.module.scss";
import classes from "./styles.module.scss";
import closeCircle from "assets/images/icons/closeCircle.svg";
import blueArrowRight from "assets/images/icons/blueArrowRight.svg";
import goBackFolder from "assets/images/icons/goBackFolder.svg";
import Loading from "components/Loading";

const { getModelInfo } = modelActions;

const SelectEFileModel = ({ isOpen, close, models, folders, onSelectModel, currentModelId }) => {
    const dispatch = useDispatch();

    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState("");
    const [preSelectedModel, setPreSelectedModel] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);

    const handleModelSelect = (key) => {
        if (key !== null) {
            setIsLoading(true);
            setShowError(false);
            dispatch(getModelInfo(key))
                .then((modelInfo) => {
                    if (modelInfo && modelInfo.modelDetails && modelInfo.modelDetails.lastCliRun) {
                        const { cliId = {} } = modelInfo.modelDetails.lastCliRun;
                        if (!isEmpty(cliId)) {
                            onSelectModel(key, modelInfo);
                            setIsLoading(false);
                            close();
                        } else {
                            setIsLoading(false);
                            setShowError(true);
                        }
                    } else {
                        setIsLoading(false);
                        setShowError(true);
                    }
                })
                .catch(() => {
                    setIsLoading(false);
                    setShowError(true);
                });
        }
    };

    const modelNames = !isEmpty(models)
        ? Object.entries(models)
              .map(([key, { name, parentFolderId }]) => {
                  return {
                      key,
                      name,
                      parentFolderId,
                      type: "model",
                  };
              })
              .sort((a, b) => getSortableName(a?.name).localeCompare(getSortableName(b?.name)))
        : [];

    const folderNames = !isEmpty(folders)
        ? Object.entries(folders)
              .map(([key, { name, parentFolderId }]) => {
                  return {
                      key,
                      name,
                      parentFolderId,
                      type: "folder",
                  };
              })
              .sort((a, b) => getSortableName(a?.name).localeCompare(getSortableName(b?.name)))
        : [];

    const directory = [...folderNames, ...modelNames];

    const filteredDirectory = directory
        .filter(({ parentFolderId }) => {
            return (parentFolderId == null && currentFolderId == null) || parentFolderId === currentFolderId;
        })
        .filter((item) => {
            return item.key !== currentModelId;
        });

    const preSelectedModelLabel =
        preSelectedModel != null
            ? modelNames.reduce((acc, { key, name }) => {
                  if (key === preSelectedModel) {
                      return name;
                  } else {
                      return acc;
                  }
              }, "")
            : "";

    return (
        <Dialog
            onClose={close}
            open={isOpen}
            classes={{ paperClass: `${dialogContainer} ${classes.dialogZIndex1500}`, rootClass: "selectEFileDialogRoot" }}
            BackdropProps={{
                style: { zIndex: 1450 },
                className: "selectEFileDialogBackdrop",
            }}
            style={{ zIndex: 1500 }}
            TransitionProps={{
                style: { zIndex: 1500 },
                onEnter: (node) => {
                    // Ensure z-index is set immediately when transition starts
                    if (node) {
                        node.style.zIndex = "1500";
                    }
                },
            }}
            transitionDuration={25}
        >
            <div className={headerContainer}>
                <div className={headerTextContainer}>
                    {currentFolderId && folders?.[currentFolderId] && (
                        <img
                            className={goBackImg}
                            src={goBackFolder}
                            alt="go back"
                            onClick={() => {
                                setCurrentFolderId(folders[currentFolderId].parentFolderId);
                                setSelectedFolder("");
                            }}
                        />
                    )}
                    <span className={headerText}>
                        {folders?.[currentFolderId]?.name
                            ? `${folders[currentFolderId].name}`
                            : "Select an E-file or External Model"}
                    </span>
                </div>
                <img
                    src={closeCircle}
                    alt="close circle"
                    onClick={() => {
                        setPreSelectedModel(null);
                        setCurrentFolderId(null);
                        setSelectedFolder("");
                        setShowError(false);
                        close();
                    }}
                    style={{ cursor: "pointer" }}
                />
            </div>
            <div className={foldersContainer}>
                {isEmpty(models) ? (
                    <div className={loading}>
                        <Loading message="loading" />
                    </div>
                ) : (
                    filteredDirectory.map(({ key, name, type }) => (
                        <div
                            key={key}
                            className={`
                                ${folderContainer} 
                                ${selectedFolder === key && preSelectedModel !== key && selectedFolderContainer}
                                ${preSelectedModel === key && selectedFolderContainer}
                            `}
                            onClick={
                                type === "folder"
                                    ? () => {
                                          if (selectedFolder === key) {
                                              setSelectedFolder("");
                                              setCurrentFolderId(key);
                                          } else {
                                              setSelectedFolder(key);
                                              setPreSelectedModel(null);
                                          }
                                          setShowError(false);
                                      }
                                    : () => {
                                          if (preSelectedModel === key) {
                                              setPreSelectedModel(null);
                                          } else {
                                              setPreSelectedModel(key);
                                              setSelectedFolder("");
                                          }
                                          setShowError(false);
                                      }
                            }
                        >
                            <div className={itemLabel}>
                                {type === "folder" ? (
                                    <img src={FolderIcon} alt="Folder icon" />
                                ) : (
                                    <img src={ModelIcon} alt="Model icon" />
                                )}
                                <div className={labelRowText}>
                                    <p>{name}</p>
                                </div>
                            </div>
                            {type === "folder" && (
                                <img
                                    className={selectedFolder !== key && folderImg}
                                    src={blueArrowRight}
                                    alt="blue arrow right"
                                />
                            )}
                        </div>
                    ))
                )}
            </div>
            <div className={moveToContainer}>
                <div className={moveToText}>
                    <p>{preSelectedModelLabel}</p>
                    {showError && <span className={errorMessage}>Model does not contain results</span>}
                </div>
                <Button
                    onClick={() => {
                        handleModelSelect(preSelectedModel);
                        setPreSelectedModel(null);
                    }}
                    disabled={preSelectedModel === null || isLoading}
                >
                    {isLoading ? "Loading..." : "Select Model"}
                </Button>
            </div>
        </Dialog>
    );
};

export default SelectEFileModel;
